<template>
    <b-modal :visible="value" @change="onVisibleChange" title="AR Payments & Adjustments" size="lg" hide-footer>
        <!-- Post Payment -->
        <section v-if="canPostPayment" class="mb-3">
            <h6>Post Payment</h6>
            <b-row>
                <b-col cols="4">
                    <b-form-group label="Amount">
                        <b-form-input v-model.number="payment.amount" type="number" min="0" step="0.01" />
                    </b-form-group>
                </b-col>
                <b-col cols="4">
                    <b-form-group label="Method">
                        <b-select v-model.number="payment.paymentMethod" :options="paymentMethodOptions" />
                    </b-form-group>
                </b-col>
                <b-col cols="4">
                    <b-form-group v-if="payment.paymentMethod === 2" label="Check #">
                        <b-form-input v-model="payment.checkNumber" />
                    </b-form-group>
                    <b-form-group v-else-if="payment.paymentMethod === 3" label="Reference">
                        <b-form-input v-model="payment.referenceId" />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-form-group label="Notes">
                <b-form-input v-model="payment.notes" />
            </b-form-group>
            <div class="text-right">
                <b-button size="sm" variant="primary" :disabled="saving || !(payment.amount > 0)" @click="postPayment">
                    Post Payment
                </b-button>
            </div>
        </section>

        <!-- Payments list + void -->
        <section class="mb-3">
            <h6>Payments</h6>
            <b-table-simple small bordered class="mb-0">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Method</th>
                        <th class="text-right">Amount</th>
                        <th>Status</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!payments.length">
                        <td colspan="5" class="text-center">No payments</td>
                    </tr>
                    <tr v-for="p in payments" :key="p.id">
                        <td>{{ formatDate(p.created) }}</td>
                        <td>{{ paymentMethodLabel(p.paymentMethod) }}</td>
                        <td class="text-right">{{ currency(p.amount) }}</td>
                        <td>{{ paymentStatusLabel(p.status) }}</td>
                        <td class="text-right">
                            <b-button v-if="canRefund && p.status === 1" size="sm" variant="outline-danger"
                                :disabled="saving" @click="voidPayment(p)">
                                Void
                            </b-button>
                        </td>
                    </tr>
                </tbody>
            </b-table-simple>
        </section>

        <!-- Adjustments -->
        <section v-if="canAdjust" class="mb-3">
            <h6>Post Adjustment</h6>
            <b-row>
                <b-col cols="4">
                    <b-form-group label="Type">
                        <b-select v-model.number="adjustment.transactionType" :options="adjustmentTypeOptions" />
                    </b-form-group>
                </b-col>
                <b-col cols="4">
                    <b-form-group label="Direction">
                        <b-select v-model="adjustment.direction" :options="directionOptions" />
                    </b-form-group>
                </b-col>
                <b-col cols="4">
                    <b-form-group label="Amount">
                        <b-form-input v-model.number="adjustment.amount" type="number" min="0" step="0.01" />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="4">
                    <b-form-group label="Reason Code">
                        <b-form-input v-model="adjustment.reasonCode" />
                    </b-form-group>
                </b-col>
                <b-col cols="8">
                    <b-form-group label="Notes">
                        <b-form-input v-model="adjustment.notes" />
                    </b-form-group>
                </b-col>
            </b-row>
            <small class="text-muted">A charge increases the balance owed; a credit reduces it.</small>
            <div class="text-right">
                <b-button size="sm" variant="primary"
                    :disabled="saving || !(adjustment.amount > 0) || !adjustment.reasonCode" @click="postAdjustment">
                    Post Adjustment
                </b-button>
            </div>
        </section>

        <!-- Write-off -->
        <section v-if="canWriteOff" class="mb-2">
            <h6>Write Off</h6>
            <b-row>
                <b-col cols="4">
                    <b-form-group label="Amount">
                        <b-form-input v-model.number="writeOff.amount" type="number" min="0" step="0.01" />
                    </b-form-group>
                </b-col>
                <b-col cols="4">
                    <b-form-group label="Reason Code">
                        <b-form-input v-model="writeOff.reasonCode" />
                    </b-form-group>
                </b-col>
                <b-col cols="4">
                    <b-form-group label="Notes">
                        <b-form-input v-model="writeOff.notes" />
                    </b-form-group>
                </b-col>
            </b-row>
            <div class="text-right">
                <b-button size="sm" variant="danger"
                    :disabled="saving || !(writeOff.amount > 0) || !writeOff.reasonCode" @click="postWriteOff">
                    Write Off
                </b-button>
            </div>
        </section>

        <b-modal v-model="showVoidModal" title="Void Payment" ok-title="Void Payment" ok-variant="danger"
            :ok-disabled="!voidReason || saving" @ok="confirmVoid">
            <p v-if="voidTarget">Void payment of {{ currency(voidTarget.amount) }}?</p>
            <b-form-group label="Reason">
                <b-form-textarea v-model="voidReason" rows="2"
                    placeholder="Reason for voiding this payment" />
            </b-form-group>
        </b-modal>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
    ARPaymentView,
    ARPaymentMethodOptions,
    ARAdjustmentTypeOptions,
    arPaymentMethodLabel,
    arPaymentStatusLabel,
} from "@/models/AR";
import axios from "axios";
import moment from "moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    name: "ARPaymentsModal",
})
export default class ARPaymentsModal extends Vue {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop({ default: null }) accountId!: number | null;

    private saving = false;
    private payments: ARPaymentView[] = [];
    private showVoidModal = false;
    private voidTarget: ARPaymentView | null = null;
    private voidReason = "";
    private paymentMethodOptions = ARPaymentMethodOptions;
    private adjustmentTypeOptions = ARAdjustmentTypeOptions;
    private directionOptions = [
        { value: "charge", text: "Charge (increases balance)" },
        { value: "credit", text: "Credit (reduces balance)" },
    ];

    private payment = this.emptyPayment();
    private adjustment = this.emptyAdjustment();
    private writeOff = this.emptyWriteOff();

    get canPostPayment(): boolean {
        return this.hasPermission("Accounts Receivable/Payment/Post");
    }
    get canRefund(): boolean {
        return this.hasPermission("Accounts Receivable/Payment/Refund");
    }
    get canAdjust(): boolean {
        return this.hasPermission("Accounts Receivable/Transaction/Post Adjustment");
    }
    get canWriteOff(): boolean {
        return this.hasPermission("Accounts Receivable/Transaction/Write Off");
    }

    private hasPermission(code: string): boolean {
        return this.$permissions.some(p => p.permissionCodeName === code);
    }

    @Watch("value")
    onShow(visible: boolean): void {
        if (!visible) return;
        this.payment = this.emptyPayment();
        this.adjustment = this.emptyAdjustment();
        this.writeOff = this.emptyWriteOff();
        this.loadPayments();
    }

    private emptyPayment() {
        return { amount: 0, paymentMethod: 1, checkNumber: "", referenceId: "", notes: "" };
    }
    private emptyAdjustment() {
        return { transactionType: 4, direction: "charge", amount: 0, reasonCode: "", notes: "" };
    }
    private emptyWriteOff() {
        return { amount: 0, reasonCode: "", notes: "" };
    }

    private async loadPayments(): Promise<void> {
        if (!this.accountId) return;
        try {
            const response = await axios.get<ARPaymentView[]>(`/AR/accounts/${this.accountId}/payments`);
            this.payments = response.data ?? [];
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    async postPayment(): Promise<void> {
        if (!this.accountId) return;
        try {
            this.saving = true;
            await axios.post(`/AR/accounts/${this.accountId}/payments`, {
                amount: this.payment.amount,
                paymentMethod: this.payment.paymentMethod,
                checkNumber: this.payment.paymentMethod === 2 ? this.payment.checkNumber : null,
                referenceId: this.payment.paymentMethod === 3 ? this.payment.referenceId : null,
                notes: this.payment.notes,
            });
            this.$notification(NotificationOptions.success("Payment posted."));
            this.payment = this.emptyPayment();
            await this.loadPayments();
            this.$emit("saved");
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    voidPayment(p: ARPaymentView): void {
        this.voidTarget = p;
        this.voidReason = "";
        this.showVoidModal = true;
    }

    async confirmVoid(bvModalEvent: { preventDefault: () => void }): Promise<void> {
        if (!this.voidReason) {
            bvModalEvent.preventDefault();
            return;
        }
        if (!this.voidTarget) return;
        try {
            this.saving = true;
            await axios.post(`/AR/payments/${this.voidTarget.id}/void`, { reasonCode: this.voidReason });
            this.$notification(NotificationOptions.success("Payment voided."));
            this.showVoidModal = false;
            await this.loadPayments();
            this.$emit("saved");
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    async postAdjustment(): Promise<void> {
        if (!this.accountId) return;
        const signed = this.adjustment.direction === "credit"
            ? -Math.abs(this.adjustment.amount)
            : Math.abs(this.adjustment.amount);
        try {
            this.saving = true;
            await axios.post(`/AR/accounts/${this.accountId}/adjustments`, {
                transactionType: this.adjustment.transactionType,
                amount: signed,
                reasonCode: this.adjustment.reasonCode,
                notes: this.adjustment.notes,
            });
            this.$notification(NotificationOptions.success("Adjustment posted."));
            this.adjustment = this.emptyAdjustment();
            this.$emit("saved");
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    async postWriteOff(): Promise<void> {
        if (!this.accountId) return;
        try {
            this.saving = true;
            // A write-off reduces the balance owed (a credit).
            await axios.post(`/AR/accounts/${this.accountId}/write-offs`, {
                amount: -Math.abs(this.writeOff.amount),
                reasonCode: this.writeOff.reasonCode,
                notes: this.writeOff.notes,
            });
            this.$notification(NotificationOptions.success("Write-off posted."));
            this.writeOff = this.emptyWriteOff();
            this.$emit("saved");
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    paymentMethodLabel(method?: number): string {
        return arPaymentMethodLabel(method);
    }
    paymentStatusLabel(status?: number): string {
        return arPaymentStatusLabel(status);
    }
    currency(value?: number | null): string {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);
    }
    formatDate(date?: string): string {
        return date ? moment.utc(date).local().format("L") : "";
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }
}
</script>

<style scoped></style>
